/**
 * İkon seti — Linear dili.
 *
 * =============================================================================
 * NEDEN ELDE ÇİZİLDİ, NEDEN KÜTÜPHANE DEĞİL
 * -----------------------------------------------------------------------------
 * Kasa 7 gün offline çalışabilmeli (NF-13) ve soğuk açılış < 4 sn olmalı
 * (NF-06). Yazı tipleri bile CDN'den değil `@fontsource` ile PAKETE GÖMÜLÜR;
 * ikon için dış istek atmak aynı iki maddeyi birden bozar. Bir ikon paketini
 * bağımlılık olarak eklemek de 1000+ ikonun tamamını sürüm yükseltmelerinde
 * taşımak demektir — burada 30 ikon kullanılıyor.
 *
 * ÇİZİM KURALLARI
 * · 20x20 ızgara, içerik 2.4–17.6 arasında yaşar. Gezinmede 16px çizilir.
 * · Kontur 1.5px — tasarım sisteminin tek kenarlık ağırlığıyla aynı.
 * · Dolgu YOK, her şey konturdur. `currentColor` — renk metinden miras alınır,
 *   böylece koyu temada ayrı bir ikon paleti gerekmez.
 * · Köşeler yuvarlak birleşir: 18px'te keskin köşe kırılır ve kirli görünür.
 *
 * ERİŞİLEBİLİRLİK
 * İkonlar VARSAYILAN OLARAK DEKORATİFTİR (`aria-hidden`). Bir ikon metin
 * etiketinin YERİNE geçmez; menüde de, butonda da yazı kalır (WCAG 1.4.1 —
 * şekil tek başına anlam taşımaz). Yalnızca yanında yazı olmayan bir yerde
 * kullanılıyorsa `etiket` verilir; o zaman `role="img"` + `<title>` üretilir.
 * =============================================================================
 */
import type { ReactNode } from "react";

export interface IkonOzellikleri {
  /** Kenar uzunluğu (px). Gezinmede ve butonda 16, boş durumda 32. */
  boyut?: number;
  className?: string;
  /**
   * Verilirse ikon ANLAM TAŞIR ve ekran okuyucuya duyurulur. Yanında yazı
   * varsa VERİLMEZ — aynı şey iki kez okunur.
   */
  etiket?: string;
}

export type IkonBileseni = (o: IkonOzellikleri) => ReactNode;

/**
 * İkon fabrikası. Kontur kuralları tek yerde durur; bir ikon eklemek yalnızca
 * çizim eklemektir — böylece set zamanla ağırlık/uç stili bakımından dağılmaz.
 */
function yap(ad: string, cizim: ReactNode): IkonBileseni {
  const Ikon = ({ boyut = 18, className = "", etiket }: IkonOzellikleri) => (
    <svg
      width={boyut}
      height={boyut}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${className}`}
      {...(etiket
        ? { role: "img", "aria-label": etiket }
        : { "aria-hidden": true, focusable: false })}
    >
      {etiket && <title>{etiket}</title>}
      {cizim}
    </svg>
  );
  Ikon.displayName = `Ikon${ad}`;
  return Ikon;
}

// ---------------------------------------------------------------------------
// İzleme
// ---------------------------------------------------------------------------

/** Gösterge — kadran ve ibre. */
export const IkonGosterge = yap(
  "Gosterge",
  <>
    <path d="M3.2 15.2a6.8 6.8 0 1 1 13.6 0" />
    {/* Taksimatlar kadranın İÇİNDE durur: yayın üstüne binince 18 px'te
        çizgiler birbirine yapışır ve ikon kirlenir. */}
    <path d="M6.5 11.7l.7.7M10 10.3v.9M13.5 11.7l-.7.7" />
    <path d="M10 15.2l3.4-4.9" />
    <circle cx="10" cy="15.2" r="1.1" />
  </>,
);

/** Terminal sağlığı — cihaz ve nabız. */
export const IkonTerminal = yap(
  "Terminal",
  <>
    <rect x="4.5" y="2.5" width="11" height="15" rx="1.8" />
    <path d="M7.2 5.6h5.6" />
    <path d="M6.8 12.2h1.9l1-2.4 1.6 4.2 1-1.8h1.9" />
  </>,
);

// ---------------------------------------------------------------------------
// Denetim
// ---------------------------------------------------------------------------

/** Mutabakat — terazi. İki kefe eşitlenene kadar iş bitmez. */
export const IkonMutabakat = yap(
  "Mutabakat",
  <>
    <path d="M10 3.4v13.1" />
    <path d="M4.6 6.6h10.8" />
    <path d="M5.4 6.6v4.6M14.6 6.6v4.6" />
    <path d="M2.9 11.6h5a2.5 2.5 0 0 1-5 0z" />
    <path d="M12.1 11.6h5a2.5 2.5 0 0 1-5 0z" />
    <path d="M7 16.5h6" />
  </>,
);

/** Anomali — uyarı üçgeni. */
export const IkonAnomali = yap(
  "Anomali",
  <>
    <path d="M10.9 3.9l6.7 11.6a1.1 1.1 0 0 1-.9 1.6H3.3a1.1 1.1 0 0 1-.9-1.6L9.1 3.9a1.1 1.1 0 0 1 1.8 0z" />
    <path d="M10 8.3v3.4" />
    <path d="M10 14.4h.01" />
  </>,
);

/** Denetim kaydı — kaydın üzerindeki büyüteç. */
export const IkonDenetimKaydi = yap(
  "DenetimKaydi",
  <>
    <path d="M4.3 2.6h6.4l3.5 3.5v3.2" />
    <path d="M4.3 2.6v14.8h4.4" />
    <path d="M10.4 2.6v3.8h3.8" />
    <path d="M7 8.6h4.2M7 11.4h2.6" />
    <circle cx="13" cy="13.2" r="3" />
    <path d="M15.2 15.4l2.2 2.2" />
  </>,
);

// ---------------------------------------------------------------------------
// Raporlar
// ---------------------------------------------------------------------------

/** Z raporu — ucu yırtık fiş şeridi. */
export const IkonZRaporu = yap(
  "ZRaporu",
  <>
    <path d="M4.8 2.6h10.4v13.1l-1.73 1.5l-1.74-1.5l-1.73 1.5l-1.74-1.5l-1.73 1.5l-1.73-1.5z" />
    <path d="M7.3 6.2h5.4M7.3 9h5.4M7.3 11.8h3" />
  </>,
);

/** Kâr-zarar — eksen üzerinde eğilim çizgisi. */
export const IkonKarZarar = yap(
  "KarZarar",
  <>
    <path d="M3.4 2.8v12.4a1.4 1.4 0 0 0 1.4 1.4h12.4" />
    <path d="M6.2 13.2l3-3.6 2.4 2 3.9-5.2" />
    <path d="M12.6 6.4h3v3" />
  </>,
);

/** Ürün kârlılığı — koli ve yön oku. */
export const IkonUrunKarlilik = yap(
  "UrunKarlilik",
  <>
    <path d="M3.2 6.5L8 4l4.8 2.5v5.4L8 14.4l-4.8-2.5z" />
    <path d="M3.2 6.5L8 9l4.8-2.5M8 9v5.4" />
    <path d="M15.6 16.8v-5.6M13.6 13.2l2-2 2 2" />
  </>,
);

/** Nokta sorumluluğu — noktanın içindeki kişi. */
export const IkonNoktaSorumluluk = yap(
  "NoktaSorumluluk",
  <>
    <path d="M10 17.6s5.6-5.5 5.6-9.4a5.6 5.6 0 1 0-11.2 0c0 3.9 5.6 9.4 5.6 9.4z" />
    <circle cx="10" cy="6.7" r="1.7" />
    <path d="M7.2 11.1a3 3 0 0 1 5.6 0" />
  </>,
);

// ---------------------------------------------------------------------------
// Kişi ve kart
// ---------------------------------------------------------------------------

/** Kişiler. */
export const IkonKisiler = yap(
  "Kisiler",
  <>
    <circle cx="7.8" cy="6.9" r="2.9" />
    <path d="M2.6 16.8a5.2 5.2 0 0 1 10.4 0" />
    <path d="M13.2 4.4a2.9 2.9 0 0 1 0 5" />
    <path d="M14.6 11.6a5.2 5.2 0 0 1 2.8 4.4" />
  </>,
);

/** Kart — temassız okuma dalgalarıyla. */
export const IkonKart = yap(
  "Kart",
  <>
    <rect x="2.4" y="4.4" width="15.2" height="11.2" rx="1.8" />
    <path d="M2.4 8.2h15.2" />
    <path d="M5.4 12.4h3.4" />
    <path d="M12.6 10.4a2.4 2.4 0 0 1 0 3.4" />
    <path d="M14.4 9.2a4 4 0 0 1 0 5.8" />
  </>,
);

/** İçe aktarım — tepsiye inen ok. */
export const IkonIceAktarim = yap(
  "IceAktarim",
  <>
    <path d="M10 2.8v8.8" />
    <path d="M6.6 8.4L10 11.8l3.4-3.4" />
    <path d="M3.4 12.6v3.2a1.4 1.4 0 0 0 1.4 1.4h10.4a1.4 1.4 0 0 0 1.4-1.4v-3.2" />
  </>,
);

// ---------------------------------------------------------------------------
// Stok
// ---------------------------------------------------------------------------

/** Ürün — bantlı koli. */
export const IkonUrun = yap(
  "Urun",
  <>
    <path d="M10 2.6l6.8 3.5v7.8L10 17.4l-6.8-3.5V6.1z" />
    <path d="M3.2 6.1L10 9.6l6.8-3.5M10 9.6v7.8" />
    <path d="M6.6 4.35l6.8 3.5" />
  </>,
);

/** Stok — istiflenmiş sandıklar. */
export const IkonStok = yap(
  "Stok",
  <>
    <rect x="2.6" y="10.4" width="6.4" height="6.4" rx="0.6" />
    <rect x="11" y="10.4" width="6.4" height="6.4" rx="0.6" />
    <rect x="6.8" y="3.2" width="6.4" height="6.4" rx="0.6" />
  </>,
);

/** Mal kabul — kapağı açılmış koliye inen sevkiyat. */
export const IkonMalKabul = yap(
  "MalKabul",
  <>
    <path d="M3.6 9.4v6.2a1.4 1.4 0 0 0 1.4 1.4h10a1.4 1.4 0 0 0 1.4-1.4V9.4" />
    {/* Kapaklar DIŞA açılır: içe kıvrılınca aşağı inen okun ucuyla kesişir ve
        16 px'te tek bir leke olur. */}
    <path d="M3.6 9.4L2.4 8.2M16.4 9.4l1.2-1.2" />
    <path d="M10 2.8v6.4" />
    <path d="M7.3 6.5L10 9.2l2.7-2.7" />
  </>,
);

/** Transfer — karşılıklı iki ok. */
export const IkonTransfer = yap(
  "Transfer",
  <>
    <path d="M3.4 7.4h11.4M11.8 4.4l3 3-3 3" />
    <path d="M16.6 13H5.2M8.2 10l-3 3 3 3" />
  </>,
);

/** Sayım — pano üzerinde işaretlenen raf çetelesi. */
export const IkonSayim = yap(
  "Sayim",
  <>
    <rect x="3.8" y="4" width="12.4" height="13.4" rx="1.6" />
    <path d="M7.8 4V3.4a1 1 0 0 1 1-1h2.4a1 1 0 0 1 1 1V4" />
    <path d="M6.8 9.4l1.4 1.4 2.8-3" />
    <path d="M6.8 13.6h6.4M6.8 15.6h3.4" />
  </>,
);

/** Karantina — kilitli sandık. Mal ortada, ama kimse dokunamaz. */
export const IkonKarantina = yap(
  "Karantina",
  <>
    <rect x="2.8" y="8.6" width="14.4" height="8.6" rx="1.4" />
    <path d="M6.4 8.6V6a3.6 3.6 0 0 1 7.2 0v2.6" />
    <path d="M10 11.8v2.4" />
  </>,
);

/** İmha — çöp kutusu. Karantinadan farkı: buradan geri dönüş yok. */
export const IkonImha = yap(
  "Imha",
  <>
    <path d="M3.6 5.6h12.8" />
    <path d="M7.6 5.6V4.2a1.4 1.4 0 0 1 1.4-1.4h2a1.4 1.4 0 0 1 1.4 1.4v1.4" />
    <path d="M5.2 5.6l.8 10.2a1.6 1.6 0 0 0 1.6 1.5h4.8a1.6 1.6 0 0 0 1.6-1.5l.8-10.2" />
    <path d="M8.6 9v4.6M11.4 9v4.6" />
  </>,
);

// ---------------------------------------------------------------------------
// Muhasebe
// ---------------------------------------------------------------------------

/** Cari hesaplar — açık defter. Ürünün kendi metaforu. */
export const IkonCari = yap(
  "Cari",
  <>
    <path d="M10 5.4C8.3 3.9 6.2 3.2 2.8 3.2v10.9c3.4 0 5.5.7 7.2 2.2 1.7-1.5 3.8-2.2 7.2-2.2V3.2c-3.4 0-5.5.7-7.2 2.2z" />
    <path d="M10 5.4v10.9" />
  </>,
);

/** Gelir / gider — giren ve çıkan. Defter mürekkebi kuralının şekli. */
export const IkonGelirGider = yap(
  "GelirGider",
  <>
    <path d="M6.4 16.6V4M3.4 7l3-3 3 3" />
    <path d="M13.6 3.4v12.6M10.6 13l3 3 3-3" />
  </>,
);

/** Yükleme onayı — onaylanmış para. */
export const IkonYuklemeOnay = yap(
  "YuklemeOnay",
  <>
    <rect x="2.4" y="4.6" width="15.2" height="10.8" rx="1.4" />
    <path d="M6.2 10.2l2.4 2.4 5.2-5.4" />
  </>,
);

/** Yükleme mutabakatı — banka ekstresi tarafı. */
export const IkonYuklemeMutabakati = yap(
  "YuklemeMutabakati",
  <>
    <path d="M2.6 8.2h14.8L10 3.2z" />
    <path d="M4.8 8.2v6.2M8.2 8.2v6.2M11.8 8.2v6.2M15.2 8.2v6.2" />
    <path d="M3.4 14.4h13.2M2.6 17h14.8" />
  </>,
);

// ---------------------------------------------------------------------------
// Tanımlar
// ---------------------------------------------------------------------------

/** Nokta — satış noktası / depo konumu. */
export const IkonNokta = yap(
  "Nokta",
  <>
    <path d="M10 17.6s5.6-5.5 5.6-9.4a5.6 5.6 0 1 0-11.2 0c0 3.9 5.6 9.4 5.6 9.4z" />
    <circle cx="10" cy="8.2" r="2.1" />
  </>,
);

/** Personel — yaka kartı. */
export const IkonPersonel = yap(
  "Personel",
  <>
    <rect x="3" y="3.4" width="14" height="13.4" rx="1.6" />
    <path d="M7.6 3.4V2.2h4.8v1.2" />
    <circle cx="10" cy="8.8" r="2.2" />
    <path d="M6.4 14.6a3.8 3.8 0 0 1 7.2 0" />
  </>,
);

/**
 * Ayarlar — DİŞLİ.
 *
 * Işınları merkeze bağlanmamış bir yıldız olarak çizmek kolaydır ama o zaman
 * ikon GÜNEŞ olur ve üst bardaki açık tema düğmesiyle karışır. Bu yüzden
 * dişler tek bir kapalı konturla, düz tepeli olarak çizilir: 6 diş, dış
 * yarıçap 7.4, kök yarıçapı 5.4.
 */
export const IkonAyarlar = yap(
  "Ayarlar",
  <>
    <path d="M7.8 5.07L8.34 2.79h3.33l.53 2.28 0.97.56 2.24-.68 1.67 2.89-1.71 1.6v1.12l1.71 1.6-1.67 2.89-2.24-.68-.97.56-.53 2.28H8.34l-.54-2.28-.97-.56-2.24.68-1.67-2.89 1.71-1.6V9.44L2.92 7.84l1.67-2.89 2.24.68z" />
    <circle cx="10" cy="10" r="2.6" />
  </>,
);

// ---------------------------------------------------------------------------
// Kabuk — üst bar ve genel eylemler
// ---------------------------------------------------------------------------

/** Marka — cetvel çizgili defter. */
export const IkonDefter = yap(
  "Defter",
  <>
    <rect x="3.2" y="2.6" width="13.6" height="14.8" rx="1.6" />
    <path d="M6.6 2.6v14.8" />
    <path d="M9.4 6.8h4.6M9.4 10h4.6M9.4 13.2h2.8" />
  </>,
);

/** Okul — bayraklı bina. */
export const IkonOkul = yap(
  "Okul",
  <>
    <path d="M2.6 17.4h14.8" />
    <path d="M4.4 17.4V8.2L10 4.6l5.6 3.6v9.2" />
    <path d="M8.2 17.4v-4.2h3.6v4.2" />
    <path d="M10 4.6V1.9" />
    <path d="M10 2.3h2.8l-.9 1 .9 1H10" />
  </>,
);

/** Süzgeç — huni. */
export const IkonSuzgec = yap(
  "Suzgec",
  <path d="M2.8 4.2h14.4l-5.6 6.6v5.4l-3.2 1.6v-7z" />,
);

/** Açık tema. */
export const IkonGunes = yap(
  "Gunes",
  <>
    <circle cx="10" cy="10" r="3.4" />
    <path d="M10 2.2v1.8M10 16v1.8M17.8 10H16M4 10H2.2" />
    <path d="M15.5 4.5l-1.3 1.3M5.8 14.2l-1.3 1.3M15.5 15.5l-1.3-1.3M5.8 5.8L4.5 4.5" />
  </>,
);

/** Koyu tema. */
export const IkonAy = yap(
  "Ay",
  <path d="M16.4 11.8A7 7 0 0 1 8.2 3.6a7 7 0 1 0 8.2 8.2z" />,
);

/** Sistem teması — cihaz ne diyorsa. */
export const IkonEkran = yap(
  "Ekran",
  <>
    <rect x="2.6" y="3.6" width="14.8" height="10" rx="1.4" />
    <path d="M7 17h6M10 13.6V17" />
  </>,
);

/** Oturumu kapat. */
export const IkonCikis = yap(
  "Cikis",
  <>
    <path d="M12.4 3.4H15a1.6 1.6 0 0 1 1.6 1.6v10a1.6 1.6 0 0 1-1.6 1.6h-2.6" />
    <path d="M8 13.4L11.4 10 8 6.6" />
    <path d="M11.4 10H3" />
  </>,
);

/** Ekle. */
export const IkonArti = yap("Arti", <path d="M10 4v12M4 10h12" />);

/** Aşağı şevron — açılır seçiciler ve çalışma alanı değiştirici. */
export const IkonSevron = yap("Sevron", <path d="M6 8.5 10 12.5 14 8.5" />);

/** Kopyala — panoya al (tek kullanımlık kod, token, kimlik). */
export const IkonKopyala = yap(
  "Kopyala",
  <>
    <rect x="7.25" y="7.25" width="8.5" height="8.5" rx="1.8" />
    <path d="M12.75 4.25H5.9A1.65 1.65 0 0 0 4.25 5.9v6.85" />
  </>,
);

/** Onay tiki — kopyalandı / tamamlandı geri bildirimi. */
export const IkonTik = yap("Tik", <path d="m4.5 10.5 3.5 3.5 7.5-7.5" />);

/** Arama — kenar çubuğu üstündeki hızlı erişim. */
export const IkonArama = yap(
  "Arama",
  <>
    <circle cx="9" cy="9" r="5.2" />
    <path d="M12.8 12.8 16.5 16.5" />
  </>,
);
